import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

import type { PageNode } from "@tento-ui/brand/page-model";
import { setTextOrHide, stringProp } from "./node-props";
import { tshVars } from "./tokens";
import type { InstallPlatform } from "./tsh-platform";
import { platformsProp, platformStore } from "./tsh-platform";
import { TshPlatformBar } from "./tsh-platform-bar";

// The close: the command again, and the one link worth following.
//
// It repeats the hero's one-liner rather than inventing a second call to
// action, because by this point the reader has the reasons and needs the
// command, not a differently-worded button.
//
// Including the platform bar: it repeats the hero's SELECTION too, through the
// shared store. A reader who picked Windows at the top and scrolled past six
// screens of figures should not have to notice that the command down here went
// back to macOS — that is the version they would copy, because it is the one
// they reached with their mind made up.
@component({ name: "tsh-cta" })
export class TshCta extends LonaWebComponent {
  private $heading: HTMLElement = this.$("heading");
  private $note: HTMLElement = this.$("note");
  private $command: HTMLElement = this.$("command");
  private $prompt: HTMLElement = this.$("prompt");
  private $commandText: HTMLElement = this.$("command-text");
  private $copy: HTMLElement = this.$("copy");
  private $link: HTMLAnchorElement = this.$<HTMLAnchorElement>("link");
  private $platforms: TshPlatformBar = TshPlatformBar.make();
  private platforms: InstallPlatform[] = [];
  private fallbackCommand = "";
  private command = "";
  private unsubscribe: Option<() => void> = null;

  constructor() {
    super();
    this.$("frame").insertBefore(this.$platforms, this.$command);

    GESTURE_MANAGER.addPointerEvent(this.$command, {
      onClick: () => {
        void navigator.clipboard?.writeText(this.command);
        this.$copy.textContent = "copied";
        window.setTimeout(() => (this.$copy.textContent = "copy"), 1600);
      },
    });
  }

  bind(node: PageNode): void {
    setTextOrHide(this.$heading, stringProp(node, "heading"));
    setTextOrHide(this.$note, stringProp(node, "note"));
    this.fallbackCommand = stringProp(node, "command") ?? "";
    this.platforms = platformsProp(node);
    this.$platforms.bind(this.platforms);
    this.renderCommand();

    const href = stringProp(node, "linkHref");
    const label = stringProp(node, "linkLabel");
    setTextOrHide(this.$link, label);
    if (href) this.$link.href = href;
    // A link with a label but no destination is worse than no link: it looks
    // clickable and is not.
    this.$link.toggleAttribute("hidden", !href || !label);
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribe ??= platformStore.subscribe(() => this.renderCommand());
    this.renderCommand();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /** The selected platform's prompt and command; see `tsh-hero.ts`. */
  private renderCommand(): void {
    const selected = this.platforms.find((p) => p.id === platformStore.get());
    this.command = selected?.command ?? this.fallbackCommand;
    this.$prompt.textContent = selected?.prompt ?? "$";
    this.$commandText.textContent = this.command;
    this.$command.toggleAttribute("hidden", this.command === "");
  }

  static $styles = [
    tshVars,
    css`
      :host {
        display: block;
        padding: 64px var(--tsh-gutter, 32px) 96px;
        font-family: var(--tsh-mono);
        color: var(--tsh-muted, #525252);
      }

      #frame {
        max-width: var(--tsh-panel, 680px);
      }

      h2 {
        --font-size: var(--h2-size);
        --line-height-ratio: 1.429;
        --margin-top: 0px;
        --margin-bottom: 16.7px; /* 4 box + 5.21 + 7.50 trims */
        font-weight: 700;
        color: var(--tsh-fg, #171717);
      }

      #note {
        --font-size: var(--p-size);
        --line-height-ratio: 1.75;
        --margin-top: 0px;
        --margin-bottom: 35px; /* 20 box + 7.50 + 7.50 trims */
      }

      /* The same tight 4px join as the hero: bar and command are one control. */
      tsh-platform-bar {
        display: block;
        margin-bottom: 4px;
      }

      #command {
        display: flex;
        align-items: baseline;
        gap: 12px;
        width: 100%;
        padding: 14px 16px;
        border: 1px solid var(--tsh-border, #e5e5e5);
        border-radius: var(--tsh-radius, 10px);
        background: transparent;
        font: inherit;
        font-size: calc(var(--p-size) / 0.7);
        text-align: left;
        cursor: pointer;
        color: var(--tsh-fg, #171717);
      }

      #command:hover {
        background: var(--tsh-surface, #fafafa);
      }

      #prompt {
        color: var(--tsh-faint, #9e9e9e);
      }

      #command-text {
        flex: 1;
        min-width: 0;
        overflow-x: auto;
        white-space: pre;
      }

      #copy {
        color: var(--tsh-faint, #9e9e9e);
        font-size: calc(var(--small-size) / 0.7);
        flex-shrink: 0;
      }

      #link {
        display: inline-block;
        margin-top: 18px;
        color: var(--tsh-faint, #9e9e9e);
        font-size: calc(var(--small-size) / 0.7);
        text-decoration: underline;
        text-underline-offset: 3px;
      }

      [hidden] {
        display: none !important;
      }
    `,
  ];

  static $html = html`
    <div id="frame">
      <h2 id="heading"></h2>
      <p id="note"></p>
      <button id="command" type="button">
        <span id="prompt">$</span>
        <span id="command-text"></span>
        <span id="copy">copy</span>
      </button>
      <a id="link"></a>
    </div>
  `;
}
